/**
 * 声の席の確かめ（/shoot/speech。Issue #858。索引 #775）: 同席で 2 台の iPhone（内蔵マイク）をつないだとき、逆側の席の声をどれだけ
 * 捨てられるかを収録現場で確かめる。無理なら OBS の話者ごとのトラックの経路（#782）にする、の判断の材料。
 * - 補正サーバーの GET /live/speech/recent の判定（core/live/speech.py の decide。直近 100 件・止めると消える）を 2 秒ごとに読んで
 *   ブラウザに溜める（localStorage。JSON で保存できる）
 * - 「今話している席」を選んで話すと、そのあいだに届いた判定にその席を付ける（行を押して直せる）→ 自席の声を残せた数・逆側の席に流れた数
 * - 差のしきい値・下限を動かすと、溜めた判定に当て直す（keepAt＝decide と同じ規則。補正サーバーの値は変えない）
 * - 2 台の時計のずれ: 同じ声とみなした 2 行の始まりの差と、重ならずに両席とも「相手では聞こえていない」になった組
 * 補正サーバーは収録のときだけ起きているので「始める」を押したときだけ読む。fetch を持たない環境（テストの jsdom）では読まない。
 */
import { liveUrl } from '../components/liveBase';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import ShootNav from '../components/ShootNav';
import { judgeText, sourceText } from '../components/SpeechPanel';

export const POLL_MS = 2000;
export const STORE_KEY = 'synchro.speechCheck.v1';
const MAX_ROWS = 5000;
const SHOW_ROWS = 200;
const NEAR_MS = 3000;
export const MARGINS = [0, 2, 3, 4, 5, 6, 8, 10, 12];
const SEATS = ['1', '2'];

export const kindOf = (d) => (d.audio === 'obs' ? 'obs' : 'mic');
/** 判定の通し番号は補正サーバーを起動し直すと 1 へ戻るので、声の始まりも鍵に入れる */
export const rowKey = (d) => `${d.id}:${d.start_ms ?? d.t_unix_ms ?? ''}`;

/** recent の判定を溜めた行へ足す（同じ判定は重ねない）。speaking（'1' / '2' / null）＝届いたときに話していた席 */
export function mergeDecisions(rows, decisions, speaking = null, nowMs = Date.now()) {
  const have = new Set(rows.map((r) => r.key));
  const fresh = (decisions ?? []).filter((d) => d && d.level_db != null && !have.has(rowKey(d)))
    .map((d) => ({ ...d, key: rowKey(d), channel: String(d.channel), truth: speaking, seen: nowMs }));
  return fresh.length ? [...rows, ...fresh].slice(-MAX_ROWS) : rows;
}

const diffOf = (r) => (r.other_db == null ? null : Math.round((r.level_db - r.other_db) * 10) / 10);

/** その行を、このしきい値ならその席に流すか（core/live/speech.py の decide と同じ規則。floor が null なら下限なし） */
export function keepAt(row, margin, floor = null) {
  if (floor != null && row.level_db < floor) return false;
  const diff = diffOf(row);
  return diff == null ? true : diff >= margin;
}

/** 話した席を付けた行の集計: own＝話した席の行、opp＝逆側の席の行。keep＝流した・drop＝捨てた */
export function score(rows, margin, floor = null, seat = null) {
  const out = { own: { keep: 0, drop: 0 }, opp: { keep: 0, drop: 0 } };
  rows.forEach((r) => {
    if (!r.truth || (seat && r.truth !== String(seat))) return;
    out[r.channel === r.truth ? 'own' : 'opp'][keepAt(r, margin, floor) ? 'keep' : 'drop'] += 1;
  });
  return out;
}

const median = (xs) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? (s.length % 2 ? s[(s.length - 1) / 2] : Math.round((s[s.length / 2 - 1] + s[s.length / 2]) / 2)) : null;
};

/** 同じ声とみなした 2 行の始まりの差（P1 − P2 の ms）。2 台の時計のずれ ＋ 認識の区切りのゆれ */
export function startGaps(rows) {
  const gaps = rows.filter((r) => r.other_start_ms != null && r.start_ms != null)
    .map((r) => (r.channel === '1' ? 1 : -1) * (r.start_ms - r.other_start_ms));
  return gaps.length ? { n: gaps.length, median: median(gaps), min: Math.min(...gaps), max: Math.max(...gaps) } : null;
}

/** 両席とも「相手では聞こえていない」で流した、始まりの近い 2 行（同じ声が重ならなかった疑い＝両席に流れている） */
export function missedPairs(rows, nearMs = NEAR_MS) {
  const alone = rows.filter((r) => r.decided === 'alone' && r.start_ms != null);
  const p2 = alone.filter((r) => r.channel === '2');
  return alone.filter((r) => r.channel === '1').flatMap((a) => {
    const b = p2.filter((o) => kindOf(o) === kindOf(a) && Math.abs(o.start_ms - a.start_ms) <= nearMs)
      .sort((x, y) => Math.abs(x.start_ms - a.start_ms) - Math.abs(y.start_ms - a.start_ms))[0];
    return b ? [{ a, b, gap_ms: a.start_ms - b.start_ms }] : [];
  });
}

/** 席ごとの届いた数と最後の声の時刻 */
export function seatStats(rows) {
  return Object.fromEntries(SEATS.map((s) => {
    const mine = rows.filter((r) => r.channel === s);
    return [s, { n: mine.length, last: mine.reduce((m, r) => Math.max(m, r.start_ms ?? r.t_unix_ms ?? 0), 0) || null }];
  }));
}

const nextTruth = (t) => (t === '1' ? '2' : t === '2' ? null : '1');
const clock = (t) => (t ? new Date(t).toLocaleTimeString('ja-JP', { hour12: false }) : '');
const dB = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}`);
const ratio = (c) => `${c.keep} / ${c.keep + c.drop}`;

function loadRows() {
  try { const v = JSON.parse(localStorage.getItem(STORE_KEY) ?? '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

function download(name, data) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

const TD = { padding: '3px 8px', borderBottom: '1px solid #232a3a', textAlign: 'left', verticalAlign: 'top' };
const NUM = { ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const WARN = { color: '#f0c36b', fontSize: 13 };
const NOTE = { margin: 0, fontSize: 12, color: '#8a93a8', lineHeight: 1.7 };

export default function SpeechCheck({ fetchImpl = null, pollMs = POLL_MS }) {
  usePageTitle('声の席の確かめ');
  const [on, setOn] = useState(false);
  const [rows, setRows] = useState(loadRows);
  const [speaking, setSpeaking] = useState(null);
  const [kind, setKind] = useState('mic');
  const [thresholds, setThresholds] = useState(null);
  const [margin, setMargin] = useState(null);       // null ＝ 補正サーバーの値
  const [floor, setFloor] = useState(undefined);    // undefined ＝ 補正サーバーの値、null ＝ 下限なし
  const [down, setDown] = useState(false);
  const speakingRef = useRef(null);
  speakingRef.current = speaking;
  const f = fetchImpl ?? (typeof fetch === 'function' && !navigator.userAgent.includes('jsdom') ? fetch : null);

  useEffect(() => {
    if (!on || !f) return undefined;
    const ac = new AbortController();
    let first = true;       // 開く前の判定には、話していた席を付けない
    const poll = async () => {
      try {
        const r = await f(liveUrl('/live/speech/recent?channel=1'), { signal: ac.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        const who = first ? null : speakingRef.current;
        first = false;
        setDown(false);
        setThresholds({ margin_db: body.margin_db, min_level_db: body.min_level_db });
        setRows((rs) => mergeDecisions(rs, body.decisions, who));
      } catch { if (!ac.signal.aborted) setDown(true); }
    };
    poll();
    const timer = setInterval(poll, pollMs);
    return () => { clearInterval(timer); ac.abort(); };
  }, [on, f, pollMs]);

  useEffect(() => { try { localStorage.setItem(STORE_KEY, JSON.stringify(rows)); } catch { /* 入りきらなければ画面の中だけ */ } }, [rows]);

  const serverMargin = thresholds?.margin_db?.[kind] ?? 6;
  const serverFloor = thresholds?.min_level_db?.[kind] ?? null;
  const m = margin ?? serverMargin;
  const fl = floor === undefined ? serverFloor : floor;
  const mine = useMemo(() => rows.filter((r) => kindOf(r) === kind), [rows, kind]);
  const stats = seatStats(mine);
  const gaps = startGaps(mine);
  const missed = missedPairs(mine);
  const labeled = mine.filter((r) => r.truth).length;
  const total = score(mine, m, fl);
  const setTruth = (key) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, truth: nextTruth(r.truth) } : r)));

  return (
    <>
    <ShootNav />
    <div className="page" data-testid="speech-check">
      <div className="page-header"><h2>声の席の確かめ</h2></div>
      <p style={{ fontSize: 14, lineHeight: 1.7, margin: 0 }}>
        同席で 2 台の iPhone（TetherCam）が同じ声を聞いたとき、大きいほうの席だけに流せているかを確かめます。「始める」を押し、話す席を選んでから
        その席で「パス」「召喚」やカード名を 10〜20 回ずつ言ってください（席を切り替えたら数秒あけてから話す）。
      </p>
      <p style={NOTE} data-testid="check-howto">
        内蔵マイクで確かめるとき: <Link to="/shoot/recording">収録の設定</Link>の「音声認識の経路」を「使わない」にし、
        <Link to="/shoot/devices">TetherCam</Link> で 2 台それぞれの「音声認識を使う」・音源「内蔵マイク」を保存します。
      </p>
      <section className="card" style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => setOn((v) => !v)} data-testid="check-toggle">{on ? '止める' : '始める'}</button>
          <span style={{ fontSize: 13 }}>今話している席:</span>
          {[[null, '付けない'], ['1', 'P1'], ['2', 'P2']].map(([v, label]) => (
            <label key={label} style={{ fontSize: 14, display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="radio" name="speaking" checked={speaking === v} onChange={() => setSpeaking(v)} data-testid={`speaking-${v ?? 'none'}`} />{label}
            </label>
          ))}
          <label style={{ fontSize: 13 }}>音の元{' '}
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="check-kind">
              <option value="mic">{sourceText('mic')}</option>
              <option value="obs">{sourceText('obs')}</option>
            </select>
          </label>
          <button type="button" className="btn" disabled={!rows.length} data-testid="check-save"
            onClick={() => download(`speech-check-${new Date().toISOString().replace(/[:.]/g, '-')}.json`, { thresholds, rows })}>JSON で保存</button>
          <button type="button" className="btn" disabled={!rows.length} data-testid="check-clear"
            onClick={() => { if (window.confirm('溜めた判定を消しますか？')) setRows([]); }}>消す</button>
        </div>
        {down ? <div data-testid="check-down" style={{ color: '#ff8a8a', fontSize: 13 }}>補正サーバーに届きません（起きていないか、止まっています）</div> : null}
        <div style={{ fontSize: 13 }} data-testid="check-seats">
          {SEATS.map((s) => `P${s}: ${stats[s].n} 件${stats[s].last ? `（最後 ${clock(stats[s].last)}）` : ''}`).join('・')}・席を付けた行 {labeled} 件
        </div>
        {on && mine.length > 0 && SEATS.some((s) => !stats[s].n)
          ? <div style={WARN} data-testid="check-one-side">片方の席からしか届いていません（もう 1 台の音声認識と、送っているチャンネルを確かめてください）</div> : null}
        <p style={NOTE}>判定は補正サーバーが直近 100 件だけ持ち、止めると消えます。ここに溜めたぶんはこのブラウザに残ります（保存は「JSON で保存」）。</p>
      </section>

      <section className="card" style={{ display: 'grid', gap: 8 }} data-testid="check-result">
        <h3 className="card-title" style={{ margin: 0 }}>結果</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <label>差のしきい値{' '}
            <input type="number" step="0.5" min="0" value={m} onChange={(e) => setMargin(e.target.value === '' ? null : Number(e.target.value))}
              style={{ width: 70 }} data-testid="check-margin" /> dB
          </label>
          <label>下限{' '}
            <input type="number" step="1" value={fl ?? ''} placeholder="なし" onChange={(e) => setFloor(e.target.value === '' ? null : Number(e.target.value))}
              style={{ width: 70 }} data-testid="check-floor" /> dB
          </label>
          <button type="button" className="btn" onClick={() => { setMargin(null); setFloor(undefined); }} data-testid="check-reset">補正サーバーの値に戻す</button>
          <span style={{ color: '#8a93a8' }}>補正サーバー: 差 {serverMargin} dB・下限 {serverFloor == null ? 'なし' : `${serverFloor} dB`}</span>
        </div>
        {labeled === 0 ? <div style={{ fontSize: 13, color: '#b8c0d4' }}>話す席を選んでから話すと、ここに数が出ます。</div> : (
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr><th style={TD}>話した席</th><th style={NUM}>自席に残した</th><th style={NUM}>逆側の席に流れた</th></tr></thead>
            <tbody>
              {[...SEATS, null].map((s) => {
                const c = s ? score(mine, m, fl, s) : total;
                return (
                  <tr key={s ?? 'all'} data-testid={`check-score-${s ?? 'all'}`}>
                    <td style={TD}>{s ? `P${s}` : '合計'}</td>
                    <td style={NUM}>{ratio(c.own)}</td>
                    <td style={{ ...NUM, color: c.opp.keep ? '#ff8a8a' : undefined }}>{ratio(c.opp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {labeled > 0 ? (
          <>
            <div style={{ fontSize: 12, color: '#8a93a8' }}>差のしきい値ごと（下限は上の値。合計）</div>
            <table style={{ borderCollapse: 'collapse', fontSize: 13 }} data-testid="check-margins">
              <thead><tr><th style={NUM}>差</th><th style={NUM}>自席に残した</th><th style={NUM}>逆側の席に流れた</th></tr></thead>
              <tbody>
                {MARGINS.map((x) => {
                  const c = score(mine, x, fl);
                  return (
                    <tr key={x} style={{ background: x === m ? '#17241b' : undefined }}>
                      <td style={NUM}>{x} dB</td><td style={NUM}>{ratio(c.own)}</td>
                      <td style={{ ...NUM, color: c.opp.keep ? '#ff8a8a' : undefined }}>{ratio(c.opp)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : null}
        <p style={NOTE}>
          「逆側の席に流れた」が 0 に近く、「自席に残した」がほぼ全部になるしきい値があれば内蔵マイクで足ります。どのしきい値でも両立しなければ、
          OBS のピンマイクの経路（収録の設定の音声認識）にします。
        </p>
      </section>

      <section className="card" style={{ display: 'grid', gap: 6 }} data-testid="check-clock">
        <h3 className="card-title" style={{ margin: 0 }}>2 台の時計のずれ</h3>
        <div style={{ fontSize: 13 }} data-testid="check-gaps">
          {gaps ? `同じ声とみなした行の始まりの差（P1 − P2）: 中央 ${gaps.median} ms・最小 ${gaps.min} ms・最大 ${gaps.max} ms（${gaps.n} 行）`
            : '同じ声とみなした行がまだありません（補正サーバーが古いと、始まりの差は出ません）'}
        </div>
        {missed.length ? (
          <div style={WARN} data-testid="check-missed">
            両席とも「相手の席では聞こえていない」で流した、始まりの近い行が {missed.length} 組あります（同じ声が重ならず、両席に流れた疑い）:
            {' '}{missed.slice(-5).map((p) => `「${p.a.text}」/「${p.b.text}」${p.gap_ms} ms`).join('、')}
          </div>
        ) : null}
        <p style={NOTE}>同じ声かどうかは声の区間の重なりで決めるので、2 台の時計が一言の長さほどずれると突き合わせられず、両席に流れます。</p>
      </section>

      <section className="card" style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        <h3 className="card-title" style={{ margin: 0 }}>判定（新しい順。{Math.min(mine.length, SHOW_ROWS)} / {mine.length} 件）</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead>
              <tr>
                <th style={TD}>時刻</th><th style={TD}>席</th><th style={TD}>文字</th><th style={NUM}>大きさ</th><th style={NUM}>相手</th><th style={NUM}>差</th>
                <th style={TD}>補正サーバーの判定</th><th style={TD}>上のしきい値なら</th><th style={TD}>話した席</th>
              </tr>
            </thead>
            <tbody>
              {mine.slice(-SHOW_ROWS).reverse().map((r) => {
                const keep = keepAt(r, m, fl);
                const wrong = r.truth && keep !== (r.channel === r.truth);
                return (
                  <tr key={r.key} data-testid="check-row" style={{ color: r.keep ? undefined : '#7d8494' }}>
                    <td style={TD}>{clock(r.start_ms ?? r.t_unix_ms)}</td>
                    <td style={TD}>P{r.channel}</td>
                    <td style={{ ...TD, overflowWrap: 'anywhere' }}>{r.text}</td>
                    <td style={NUM}>{dB(r.level_db)}</td>
                    <td style={NUM}>{dB(r.other_db)}</td>
                    <td style={NUM}>{dB(diffOf(r))}</td>
                    <td style={TD}>{judgeText(r, thresholds ?? {})}</td>
                    <td style={{ ...TD, color: wrong ? '#ff8a8a' : undefined }}>{keep ? '流す' : '捨てる'}{wrong ? '（違う）' : ''}</td>
                    <td style={TD}>
                      <button type="button" className="btn" onClick={() => setTruth(r.key)} data-testid="check-truth" title="押すと P1 → P2 → 付けない">
                        {r.truth ? `P${r.truth}` : '—'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
    </>
  );
}
